// PostgREST-safe case-insensitive substring search across several columns,
// for use with postgrest-js `.or(...)`.
//
// The operator is `imatch` (Postgres `~*`, case-insensitive POSIX regex), NOT
// `ilike`: PostgREST rewrites every `*` of a like/ilike value into the `%`
// wildcard (SqlFragment.hs, applied AFTER unquoting, so quoting does not
// protect it) — a literal `*` can therefore never be matched through
// like/ilike. Articles are routinely named with `*` (sections de câble :
// « CABLE R2V 3*2.5 »), so the search term must match literally, `*` included.
// `imatch` values reach SQL untouched, and a regex-escaped term gives exact
// "contains" semantics for every character (`*`, `%`, `_`, …).
//
// A user-typed term is escaped at two layers, in this order:
//   1. regex: backslash-prefix the POSIX metacharacters so each char matches
//      literally — unanchored, so the match is "contains", like `%term%` was;
//   2. logic tree: the `.or()` argument is a PostgREST logic tree where `,`
//      `(` `)` are syntax — wrap the whole pattern in double quotes (the
//      official PostgREST mechanism for values containing reserved
//      characters) and backslash-escape `\` and `"` inside the quotes.
// Bare-backslash escaping of `,`/`(`/`)` (an even earlier approach in the
// article repository) is NOT valid logic-tree syntax: PostgREST answers
// PGRST100 "failed to parse logic tree", the whole search request 400s and
// the list renders empty.
export function orContainsAnyColumn(columns: readonly string[], term: string): string {
  const regex = term.replace(/[.^$*+?()[\]{}\\|]/g, '\\$&');
  const quoted = `"${regex.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  return columns.map((column) => `${column}.imatch.${quoted}`).join(',');
}

/**
 * Égalité EXACTE sur l'une de plusieurs colonnes (`col.eq.valeur,…`).
 *
 * Pas de `imatch` ici : un code-barre scanné se cherche au caractère près, et
 * `eq` est le seul opérateur que sert un index btree — c'est ce qui permet à la
 * recherche de rester en O(log n) quand le catalogue grossit, là où les index
 * GIN trgm ne répondent qu'aux « contains ».
 *
 * Seule la deuxième couche d'échappement d'`orContainsAnyColumn` s'applique :
 * l'argument de `.or()` est un arbre logique PostgREST où `,` `(` `)` sont de la
 * syntaxe, donc la valeur part entre guillemets, `\` et `"` doublés.
 */
export function orEqualsAnyColumn(columns: readonly string[], value: string): string {
  const quoted = `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  return columns.map((column) => `${column}.eq.${quoted}`).join(',');
}

/**
 * Égalité exacte mais INSENSIBLE À LA CASSE, via un `imatch` ancré (`^…$`).
 *
 * Pourquoi pas `ilike` : PostgREST réécrit chaque `*` d'une valeur like/ilike en
 * joker `%` (après déquotage — le quoting ne protège donc pas), et `%`/`_` s'y
 * comportent aussi en jokers. Un code article contenant l'un de ces caractères
 * matcherait alors bien plus que lui-même. `imatch` part en SQL intact ; le
 * terme est regex-échappé puis ancré, ce qui donne une vraie égalité.
 *
 * ⚠ Un `~*` ancré ne se sert PAS d'un index btree : ne l'utiliser qu'en repli
 * d'`orEqualsAnyColumn`, jamais comme chemin principal.
 */
export function orEqualsCaseInsensitiveAnyColumn(columns: readonly string[], value: string): string {
  const regex = value.replace(/[.^$*+?()[\]{}\\|]/g, '\\$&');
  const anchored = `^${regex}$`;
  const quoted = `"${anchored.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  return columns.map((column) => `${column}.imatch.${quoted}`).join(',');
}
